import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../firebase/config';
import { StudentUser } from '../types';

export interface ReferredUser {
  uid: string;
  name: string;
  email: string;
  createdAt: Date;
  status: string;
  program?: string;
}

export const useReferredUsers = () => {
  const { currentUser } = useAuth();
  const [referredUsers, setReferredUsers] = useState<ReferredUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch users referred by the current user
  const fetchReferredUsers = useCallback(async () => {
    if (!currentUser) return;

    setLoading(true);
    setError(null);

    try {
      // Query users where referredBy equals current user's ID
      const usersRef = collection(db, 'users');
      const q = query(
        usersRef, 
        where('referredBy', '==', currentUser.uid),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const users: ReferredUser[] = [];
      
      querySnapshot.forEach((doc) => {
        const userData = doc.data();
        users.push({
          uid: doc.id,
          name: userData.name || 'Unknown User',
          email: userData.email,
          createdAt: userData.createdAt?.toDate() || new Date(),
          status: userData.status || 'Unknown',
          program: userData.program || null,
        });
      });
      
      setReferredUsers(users);
    } catch (err) {
      console.error('Error fetching referred users:', err);
      setError('Failed to load referred users');
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  // Load referred users when user changes
  useEffect(() => {
    if (currentUser) {
      fetchReferredUsers();
    } else {
      setReferredUsers([]);
    }
  }, [currentUser, fetchReferredUsers]);

  return {
    referredUsers,
    loading,
    error,
    fetchReferredUsers,
    totalReferred: referredUsers.length,
  };
};
